<script>
const NON_TEXT_SCOPED_SLOTS = new Set([
  'index', 'action', 'photo', 'certificatePhoto', 'status', 'taskStatus', 'loginStatus',
  'operResult', 'risk', 'result', 'icon', 'visible', 'principalPhone', 'phone',
  'certificateNo', 'license', 'licenseNo', 'foodLicenseNo', 'foodBizLicenseNo',
  'unifiedSocialCreditCode', 'uscc', 'socialCreditCode', 'code'
])

export default {
  name: 'EllipsisTable',
  inheritAttrs: false,
  props: {
    emptyText: {
      type: String,
      default: '--'
    }
  },
  data() {
    return {
      fixedRightShadowHidden: true,
      scrollBody: null,
      autoScrollY: 0,
      autoScrollFrame: null,
      layoutResizeObserver: null,
      observedLayoutElements: [],
      layoutTransitionRoot: null
    }
  },
  mounted() {
    this.bindResize()
    this.bindLayoutObserver()
    this.bindScrollBody()
    this.scheduleAutoScrollY()
  },
  updated() {
    this.bindLayoutObserver()
    this.bindScrollBody()
    this.scheduleAutoScrollY()
  },
  beforeDestroy() {
    this.unbindResize()
    this.unbindLayoutObserver()
    this.unbindScrollBody()
    this.cancelAutoScrollY()
  },
  methods: {
    normalizeColumns(columns) {
      return (columns || []).map((column, index) => {
        const nextColumn = Object.assign({}, column)
        if (!this.hasTableRows()) delete nextColumn.fixed
        if (nextColumn.sorter === true) {
          nextColumn.sorter = this.createLocalSorter(nextColumn.dataIndex)
        }
        if (nextColumn.children && nextColumn.children.length) {
          nextColumn.children = this.normalizeColumns(nextColumn.children)
          return nextColumn
        }

        if (this.isIndexColumn(nextColumn)) {
          nextColumn.align = nextColumn.align || 'center'
          nextColumn.customHeaderCell = this.mergeCustomHeaderCell(nextColumn.customHeaderCell, 'ellipsis-table-index-th')
          nextColumn.customCell = this.mergeCustomCell(nextColumn.customCell, 'ellipsis-table-index-td')
        }

        if (this.shouldWrapScopedSlot(nextColumn)) {
          nextColumn.customCell = this.mergeCustomCell(nextColumn.customCell)
          return nextColumn
        }

        if (this.shouldWrapCustomRender(nextColumn)) {
          const customRender = nextColumn.customRender
          nextColumn.customRender = (text, record, rowIndex) => {
            const content = customRender(text, record, rowIndex)
            const title = this.getRenderedTooltipTitle(content, text)
            return this.renderTooltipCell(content, title)
          }
          nextColumn.customCell = this.mergeCustomCell(nextColumn.customCell)
          return nextColumn
        }

        if (this.shouldUseOriginalRender(nextColumn)) {
          return nextColumn
        }

        nextColumn.customRender = (text) => this.renderEllipsisCell(text)
        nextColumn.customCell = this.mergeCustomCell(nextColumn.customCell)
        return nextColumn
      })
    },
    createLocalSorter(dataIndex) {
      return (left, right) => this.compareTableValues(
        this.getColumnValue(left, dataIndex),
        this.getColumnValue(right, dataIndex)
      )
    },
    getColumnValue(record, dataIndex) {
      if (!record || dataIndex === undefined || dataIndex === null) return ''
      const path = Array.isArray(dataIndex) ? dataIndex : String(dataIndex).split('.')
      return path.reduce((value, key) => {
        if (value === undefined || value === null) return ''
        return value[key]
      }, record)
    },
    compareTableValues(left, right) {
      const leftEmpty = left === undefined || left === null || left === ''
      const rightEmpty = right === undefined || right === null || right === ''
      if (leftEmpty || rightEmpty) {
        if (leftEmpty && rightEmpty) return 0
        return leftEmpty ? 1 : -1
      }

      const leftText = String(left).trim()
      const rightText = String(right).trim()
      const numberPattern = /^[-+]?\d+(?:\.\d+)?$/
      if (numberPattern.test(leftText) && numberPattern.test(rightText)) {
        return Number(leftText) - Number(rightText)
      }
      return leftText.localeCompare(rightText, 'zh-CN', { numeric: true })
    },
    isIndexColumn(column) {
      return String(column.title || '') === '序号' || String(column.dataIndex || '') === 'index'
    },
    isNameColumn(column) {
      return /(名称|姓名)/.test(String(column && column.title || ''))
    },
    isTextScopedSlotColumn(column) {
      const slotName = String(column && column.scopedSlots && column.scopedSlots.customRender || '')
      return Boolean(slotName) && !NON_TEXT_SCOPED_SLOTS.has(slotName)
    },
    shouldWrapScopedSlot(column) {
      const hasScopedSlot = Boolean(column.scopedSlots && column.scopedSlots.customRender)
      if (!hasScopedSlot || column.ellipsisTooltip === false || column.ellipsis === false) return false
      return column.ellipsisSlot === true || this.isNameColumn(column) || this.isTextScopedSlotColumn(column)
    },
    shouldWrapCustomRender(column) {
      if (!column.customRender || column.ellipsisTooltip === false || column.ellipsis === false) return false
      return true
    },
    shouldUseOriginalRender(column) {
      if (column.ellipsisTooltip === false || column.ellipsis === false) return true
      if (column.customRender) return true
      return Boolean(column.scopedSlots && column.scopedSlots.customRender && column.ellipsisSlot !== true)
    },
    mergeCustomCell(customCell, appendClass = 'ellipsis-table-td') {
      return (record, rowIndex) => {
        const cell = customCell ? (customCell(record, rowIndex) || {}) : {}
        const data = Object.assign({}, cell)
        data.class = this.mergeClass(data.class, appendClass)
        return data
      }
    },
    mergeCustomHeaderCell(customHeaderCell, appendClass) {
      return (column) => {
        const cell = customHeaderCell ? (customHeaderCell(column) || {}) : {}
        const data = Object.assign({}, cell)
        data.class = this.mergeClass(data.class, appendClass)
        return data
      }
    },
    mergeClass(sourceClass, appendClass) {
      if (!sourceClass) return appendClass
      if (Array.isArray(sourceClass)) return sourceClass.concat(appendClass)
      return [sourceClass, appendClass]
    },
    renderEllipsisCell(text) {
      const value = this.formatCellValue(text)
      if (!value) {
        return this.$createElement('span', {
          class: 'ellipsis-table-cell'
        }, value)
      }

      return this.renderTooltipCell(value, value)
    },
    renderTooltipCell(content, title) {
      return this.$createElement('a-tooltip', {
        props: {
          title,
          overlayClassName: 'ellipsis-table-tooltip',
          placement: 'topLeft'
        }
      }, [
        this.$createElement('span', {
          class: 'ellipsis-table-cell'
        }, [content])
      ])
    },
    getRenderedTooltipTitle(content, fallback) {
      const renderedText = this.extractRenderedText(content).replace(/\s+/g, ' ').trim()
      if (renderedText) return renderedText
      return this.formatCellValue(fallback)
    },
    extractRenderedText(content) {
      if (content === undefined || content === null) return ''
      if (Array.isArray(content)) return content.map(item => this.extractRenderedText(item)).join('')
      if (typeof content !== 'object') return String(content)
      if (typeof content.text === 'string') return content.text

      const componentChildren = content.componentOptions && content.componentOptions.children
      if (componentChildren && componentChildren.length) {
        return this.extractRenderedText(componentChildren)
      }
      if (content.children && content.children.length) {
        return this.extractRenderedText(content.children)
      }
      return ''
    },
    isSensitiveTextNode(node) {
      if (!node || !node.data) return false
      const staticClass = node.data.staticClass || ''
      const dynamicClass = typeof node.data.class === 'string' ? node.data.class : ''
      return /(^|\s)(?:drs-)?masked-value(?:\s|$)/.test(`${staticClass} ${dynamicClass}`)
    },
    hasSensitiveTextNode(content) {
      const nodes = Array.isArray(content) ? content : [content]
      return nodes.some(node => this.isSensitiveTextNode(node))
    },
    wrapSensitiveTextNodes(content) {
      const isArray = Array.isArray(content)
      const nodes = isArray ? content : [content]
      let wrapped = false
      const result = nodes.map(node => {
        if (!this.isSensitiveTextNode(node)) return node
        wrapped = true
        const title = this.getRenderedTooltipTitle(node, '')
        return this.$createElement('a-tooltip', {
          props: {
            title,
            overlayClassName: 'ellipsis-table-tooltip',
            placement: 'topLeft'
          }
        }, [node])
      })
      if (isArray) return result
      return wrapped ? result[0] : content
    },
    formatCellValue(value) {
      if (value === undefined || value === null || value === '') return this.emptyText
      if (Array.isArray(value)) return value.length ? value.join('、') : this.emptyText
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },
    normalizeScopedSlots() {
      const columns = this.$attrs.columns || []
      return Object.keys(this.$scopedSlots || {}).reduce((slots, key) => {
        const column = columns.find(item => item.scopedSlots && item.scopedSlots.customRender === key)
        slots[key] = (text, record, index) => {
          const content = this.$scopedSlots[key](text, record, index)
          const hasSensitiveText = this.hasSensitiveTextNode(content)
          const normalizedContent = this.wrapSensitiveTextNodes(content)
          if (!column || hasSensitiveText || !this.shouldWrapScopedSlot(column)) {
            return normalizedContent
          }
          const title = this.getRenderedTooltipTitle(content, text)
          return this.renderTooltipCell(normalizedContent, title)
        }
        return slots
      }, {})
    },
    getScrollBody() {
      if (!this.$el) return null
      return this.$el.querySelector('.ant-table-scroll .ant-table-body') ||
        this.$el.querySelector('.ant-table-body')
    },
    bindResize() {
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', this.scheduleAutoScrollY)
      }
    },
    unbindResize() {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', this.scheduleAutoScrollY)
      }
    },
    getLayoutWatchElements() {
      if (!this.$el || !this.$el.closest) return []

      const listPage = this.findListPage()
      const elements = [
        this.$el.closest('.drs-table-wrap'),
        this.$el.closest('.content-panel'),
        listPage,
        this.$el.closest('.app-content'),
        this.$el.closest('.main-layout')
      ].filter(Boolean)

      return elements.filter((item, index) => elements.indexOf(item) === index)
    },
    isSameLayoutElements(elements) {
      return elements.length === this.observedLayoutElements.length &&
        elements.every((item, index) => item === this.observedLayoutElements[index])
    },
    bindLayoutObserver() {
      this.$nextTick(() => {
        if (!this.$el || typeof window === 'undefined') return

        const elements = this.getLayoutWatchElements()
        const transitionRoot = this.$el.closest('.bg-main') || null
        if (this.isSameLayoutElements(elements) && transitionRoot === this.layoutTransitionRoot) {
          return
        }

        this.unbindLayoutObserver()
        this.observedLayoutElements = elements

        if (window.ResizeObserver && elements.length) {
          this.layoutResizeObserver = new window.ResizeObserver(this.scheduleAutoScrollY)
          elements.forEach(element => this.layoutResizeObserver.observe(element))
        }

        if (transitionRoot) {
          this.layoutTransitionRoot = transitionRoot
          transitionRoot.addEventListener('transitionend', this.scheduleAutoScrollY)
        }
      })
    },
    unbindLayoutObserver() {
      if (this.layoutResizeObserver) {
        this.layoutResizeObserver.disconnect()
        this.layoutResizeObserver = null
      }
      if (this.layoutTransitionRoot) {
        this.layoutTransitionRoot.removeEventListener('transitionend', this.scheduleAutoScrollY)
        this.layoutTransitionRoot = null
      }
      this.observedLayoutElements = []
    },
    bindScrollBody() {
      this.$nextTick(() => {
        const body = this.getScrollBody()
        if (this.scrollBody === body) {
          this.updateFixedRightShadow()
          return
        }
        this.unbindScrollBody()
        this.scrollBody = body
        if (this.scrollBody) {
          this.scrollBody.addEventListener('scroll', this.updateFixedRightShadow, { passive: true })
        }
        this.updateFixedRightShadow()
      })
    },
    cancelAutoScrollY() {
      if (this.autoScrollFrame && typeof window !== 'undefined' && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(this.autoScrollFrame)
      }
      this.autoScrollFrame = null
    },
    scheduleAutoScrollY() {
      if (typeof window === 'undefined') return
      this.cancelAutoScrollY()
      const run = () => {
        this.autoScrollFrame = null
        this.updateAutoScrollY()
      }
      if (window.requestAnimationFrame) {
        this.autoScrollFrame = window.requestAnimationFrame(run)
      } else {
        this.$nextTick(run)
      }
    },
    hasExplicitScrollY() {
      const scroll = this.$attrs.scroll
      return scroll && Object.prototype.hasOwnProperty.call(scroll, 'y') && scroll.y !== undefined && scroll.y !== null
    },
    getResolvedScroll() {
      const baseScroll = this.$attrs.scroll
      if (this.hasExplicitScrollY()) return baseScroll
      if (!baseScroll && !this.autoScrollY) return baseScroll

      const nextScroll = Object.assign({}, baseScroll || {})
      if (this.autoScrollY) nextScroll.y = this.autoScrollY
      return nextScroll
    },
    getTableRows() {
      const dataSource = this.$attrs.dataSource || this.$attrs.data || []
      return Array.isArray(dataSource) ? dataSource : []
    },
    findListPage() {
      if (!this.$el || !this.$el.closest) return null
      return this.$el.closest('.drs-list-page, .drs-detail-page')
    },
    getAutoScrollBottomGap() {
      if (!this.$el || typeof window === 'undefined' || !window.getComputedStyle) return 12
      const detailCard = this.$el.closest('.drs-detail-card')
      if (!detailCard) return 12
      return Number.parseFloat(window.getComputedStyle(detailCard).paddingBottom) || 12
    },
    getAutoScrollY() {
      if (this.hasExplicitScrollY()) return 0

      const rows = this.getTableRows()
      const listPage = this.findListPage()
      if (!rows.length || !listPage || !this.$el || typeof window === 'undefined') return 0

      const tableWrap = this.$el.closest('.drs-table-wrap') || this.$el
      const header = this.$el.querySelector('.ant-table-thead')
      const firstRow = this.$el.querySelector('.ant-table-tbody > tr:not(.ant-table-placeholder)')
      const pagination = listPage.querySelector('.pagination-wrap, .drs-pagination')
      if (!tableWrap || !firstRow) return 0

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
      const pageRect = listPage.getBoundingClientRect()
      const tableRect = tableWrap.getBoundingClientRect()
      const pageBottom = Math.min(viewportHeight, pageRect.bottom || viewportHeight)
      const headerHeight = header ? header.getBoundingClientRect().height : 48
      const rowHeight = firstRow.getBoundingClientRect().height || 58
      const paginationHeight = pagination ? pagination.getBoundingClientRect().height : 0
      const bottomGap = this.getAutoScrollBottomGap()
      const availableBodyHeight = Math.floor(pageBottom - tableRect.top - headerHeight - paginationHeight - bottomGap)
      const fullBodyHeight = Math.ceil(rowHeight * rows.length)
      const minBodyHeight = Math.ceil(rowHeight * 3)
      const isDetailPage = listPage.classList && listPage.classList.contains('drs-detail-page')

      if (availableBodyHeight <= 0 || fullBodyHeight <= availableBodyHeight) return 0
      if (!isDetailPage && availableBodyHeight < minBodyHeight) return 0
      return availableBodyHeight
    },
    updateAutoScrollY() {
      const nextY = this.getAutoScrollY()
      if (this.autoScrollY !== nextY) {
        this.autoScrollY = nextY
      }
    },
    unbindScrollBody() {
      if (this.scrollBody) {
        this.scrollBody.removeEventListener('scroll', this.updateFixedRightShadow)
        this.scrollBody = null
      }
    },
    hasTableRows() {
      const dataSource = this.$attrs.dataSource || this.$attrs.data || []
      return Array.isArray(dataSource) && dataSource.length > 0
    },
    setFixedRightShadowHidden(hidden) {
      if (this.fixedRightShadowHidden !== hidden) {
        this.fixedRightShadowHidden = hidden
      }
    },
    updateFixedRightShadow() {
      const body = this.scrollBody
      if (!this.hasTableRows() || !body || body.scrollWidth <= body.clientWidth + 1) {
        this.setFixedRightShadowHidden(true)
        return
      }
      this.setFixedRightShadowHidden(body.scrollLeft + body.clientWidth >= body.scrollWidth - 2)
    }
  },
  render(h) {
    const props = Object.assign({}, this.$attrs, this.$props, {
      columns: this.normalizeColumns(this.$attrs.columns),
      scroll: this.getResolvedScroll()
    })

    return h('a-table', {
      props,
      on: this.$listeners,
      scopedSlots: this.normalizeScopedSlots(),
      class: {
        'ellipsis-table': true,
        'ellipsis-table-empty': !this.hasTableRows(),
        'ellipsis-table-fixed-right-shadow-hidden': this.fixedRightShadowHidden
      }
    }, this.$slots.default)
  }
}
</script>

<style scoped lang="scss">
.ellipsis-table /deep/ .ellipsis-table-td {
  max-width: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ellipsis-table /deep/ .ellipsis-table-cell {
  display: block;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ellipsis-table /deep/ .ellipsis-table-index-th,
.ellipsis-table /deep/ .ellipsis-table-index-td {
  text-align: center !important;
}

.ellipsis-table /deep/ .ellipsis-table-index-td {
  max-width: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ellipsis-table /deep/ .ant-table {
  table-layout: fixed;
}

.ellipsis-table /deep/ .ant-table-header{
  background: #f2f3f5 !important;
}

.ellipsis-table /deep/ .ant-table-thead > tr > th {
  height: 52px;
  padding: 14px 16px !important;
  color: #2f3d52;
  line-height: 24px;
  background: #f2f3f5 !important;
  border-bottom-color: #d9e4f0 !important;
}

.ellipsis-table /deep/ .ant-table-thead > tr > th.ant-table-column-sort {
  background: #eaf3ff !important;
}

.ellipsis-table /deep/ .ant-table-thead > tr > th.ant-table-column-has-sorters:hover {
  background: #edf4fc !important;
}

.ellipsis-table /deep/ .ant-table-tbody > tr > td {
  overflow: hidden;
  text-overflow: ellipsis;
}

.ellipsis-table-empty /deep/ .ant-table-scroll {
  position: relative;
}

.ellipsis-table-empty /deep/ .ant-table-body {
  min-height: 228px;
}

.ellipsis-table-empty /deep/ .ant-table-placeholder {
  position: absolute;
  top: 48px;
  right: 0;
  bottom: 8px;
  left: 0;
  height: auto;
  background: transparent;
  pointer-events: none;
}

.ellipsis-table /deep/ .primary-name {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
}

.ellipsis-table /deep/ .masked-value,
.ellipsis-table /deep/ .drs-masked-value {
  display: inline-block;
  min-width: 0 !important;
  max-width: calc(100% - 30px);
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
}

.ellipsis-table /deep/ .eye-icon,
.ellipsis-table /deep/ .drs-eye-icon {
  position: relative;
  display: inline-block;
  vertical-align: middle;
}

.ellipsis-table /deep/ .ant-table-fixed-right {
  box-shadow: -8px 0 14px -12px rgba(15, 23, 42, 0.35);
  transition: box-shadow 0.18s ease;
}

.ellipsis-table /deep/ .ant-table-fixed-right table {
  background: #fff;
}

.ellipsis-table /deep/ .ant-table-fixed-right::before {
  bottom: 0;
  box-shadow: inset -8px 0 8px -8px rgba(15, 23, 42, 0.18);
  content: '';
  pointer-events: none;
  position: absolute;
  top: 0;
  left: -8px;
  width: 8px;
  transition: opacity 0.18s ease;
}

.ellipsis-table-fixed-right-shadow-hidden /deep/ .ant-table-fixed-right {
  box-shadow: none;
}

.ellipsis-table-fixed-right-shadow-hidden /deep/ .ant-table-fixed-right::before {
  opacity: 0;
}
</style>

<style lang="scss">
.ellipsis-table-tooltip {
  max-width: 1000px;
}

.ellipsis-table-tooltip .ant-tooltip-inner {
  max-height: 360px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  overflow-wrap: anywhere;
  line-height: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/EllipsisTable/index.vue